import { useEffect, useState } from "react";
import { Scale, FileText, Sparkles, Loader2, AlertTriangle, Plus, ShieldCheck, Clock, Gavel } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { useOrg } from "@/context/OrgContext";
import { PageHeader, Scroller, Spinner, EmptyState, goldBtn } from "@/pages/_shared";
import Markdown from "@/components/Markdown";

const TABS = [["overview", "Overview"], ["matters", "Matters"], ["ai", "Legal AI"], ["templates", "Templates"]];
const MATTER_TYPES = ["Contract dispute", "Customer complaint", "Employment issue", "Supplier dispute", "Debt recovery", "IP issue", "Property matter", "Data protection", "Regulatory matter", "General"];

function Disclaimer() {
  return (
    <div className="mt-4 flex items-start gap-2 rounded-xl border border-amber-500/25 bg-amber-500/[0.06] px-4 py-3 text-xs text-amber-200/80" data-testid="legal-disclaimer">
      <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      BDVG AI is not a solicitor or law firm and this is not legal advice. Verify important matters with a qualified legal professional.
    </div>
  );
}

export default function LegalWorkspace() {
  const { can, hasFeature } = useOrg();
  const [tab, setTab] = useState("overview");
  const [dash, setDash] = useState(null);
  const [matters, setMatters] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [files, setFiles] = useState([]);
  const [showMatter, setShowMatter] = useState(false);
  const [mForm, setMForm] = useState({ title: "", type: "General", jurisdiction: "England & Wales", priority: "medium" });

  // AI tool state
  const [fileId, setFileId] = useState("");
  const [task, setTask] = useState("summary");
  const [aiOut, setAiOut] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [draft, setDraft] = useState({ doc_type: "NDA", details: "", jurisdiction: "England & Wales" });

  const load = () => {
    api.get("/legal/dashboard").then(({ data }) => setDash(data)).catch(() => setDash(null));
    api.get("/legal/matters").then(({ data }) => setMatters(data)).catch(() => {});
    api.get("/legal/templates").then(({ data }) => setTemplates(data)).catch(() => {});
    api.get("/files").then(({ data }) => setFiles(data.filter((f) => f.status === "ready"))).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const createMatter = async () => {
    if (!mForm.title) { toast.error("Title required"); return; }
    await api.post("/legal/matters", mForm);
    setShowMatter(false); setMForm({ title: "", type: "General", jurisdiction: "England & Wales", priority: "medium" });
    toast.success("Matter created"); load();
  };

  const analyze = async () => {
    if (!fileId) { toast.error("Choose a document"); return; }
    setAiBusy(true); setAiOut("");
    try { const { data } = await api.post("/legal/analyze", { file_id: fileId, task }); setAiOut(data.result); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Analysis failed"); }
    finally { setAiBusy(false); }
  };
  const makeDraft = async () => {
    setAiBusy(true); setAiOut("");
    try { const { data } = await api.post("/legal/draft", draft); setAiOut(data.draft); }
    catch (e) { toast.error(formatError(e.response?.data?.detail) || "Draft failed"); }
    finally { setAiBusy(false); }
  };

  if (!dash) return <Spinner />;

  return (
    <Scroller>
      <PageHeader title="Legal" subtitle="Understand, organise, draft and manage legal & compliance work" icon={Scale} />

      <div className="mb-5 flex gap-1 border-b border-white/8">
        {TABS.map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`legal-tab-${id}`}
            className={`border-b-2 px-4 py-2.5 text-sm transition-colors ${tab === id ? "border-gold text-white" : "border-transparent text-white/45 hover:text-white"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {[["Open Matters", dash.open_matters, Gavel], ["Contracts", dash.contracts, FileText], ["Deadlines (90d)", dash.deadlines.length, Clock], ["Awaiting Signature", dash.awaiting_signature.length, ShieldCheck]].map(([l, v, I]) => (
              <div key={l} className="rounded-2xl border border-white/8 bg-charcoal p-5" data-testid={`legal-stat-${l.split(' ')[0].toLowerCase()}`}>
                <I className="h-5 w-5 text-gold" /><p className="mt-3 font-heading text-3xl font-bold">{v}</p><p className="text-sm text-white/45">{l}</p>
              </div>
            ))}
          </div>
          <div className="rounded-2xl border border-white/8 bg-charcoal/50 p-4">
            <p className="mb-2 px-1 text-sm font-medium text-white/70">Upcoming deadlines</p>
            {dash.deadlines.length ? dash.deadlines.map((d, i) => (
              <div key={i} className="flex items-center justify-between rounded-lg px-3 py-2 text-sm">
                <span className="text-white/70">{d.doc} · <span className="capitalize text-white/40">{d.type}</span></span>
                <span className="text-gold/80">{d.date}</span>
              </div>
            )) : <p className="px-3 py-4 text-sm text-white/30">No deadlines in the next 90 days.</p>}
          </div>
          <Disclaimer />
        </div>
      )}

      {tab === "matters" && (
        <div>
          {can("legal.edit") && <button onClick={() => setShowMatter(true)} className={`${goldBtn} mb-4`} data-testid="legal-matter-new"><Plus className="h-4 w-4" /> New Matter</button>}
          {matters.length === 0 ? <EmptyState icon={Gavel} title="No legal matters yet" hint="Create a matter to organise documents, dates and tasks." /> : (
            <div className="space-y-2">
              {matters.map((m) => (
                <div key={m.id} className="flex items-center justify-between rounded-xl border border-white/8 bg-charcoal px-4 py-3" data-testid="legal-matter-row">
                  <div><p className="text-sm font-medium text-white/85">{m.title}</p><p className="text-xs text-white/40">{m.type} · {m.jurisdiction || "—"}</p></div>
                  <div className="flex items-center gap-2 text-xs">
                    {m.priority === "high" && <span className="rounded bg-red-500/15 px-2 py-0.5 text-red-300">high</span>}
                    <span className="capitalize text-white/50">{m.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "ai" && (
        <div className="space-y-6">
          <div className="rounded-2xl border border-white/8 bg-charcoal p-5">
            <p className="mb-3 font-heading font-semibold text-white/85">Review a document</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <select value={fileId} onChange={(e) => setFileId(e.target.value)} data-testid="legal-file-select" className="flex-1 rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm focus:outline-none">
                <option value="">Select an uploaded document…</option>
                {files.map((f) => <option key={f.id} value={f.id}>{f.filename}</option>)}
              </select>
              <select value={task} onChange={(e) => setTask(e.target.value)} data-testid="legal-task-select" className="rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm focus:outline-none">
                <option value="summary">Summarise</option><option value="clauses">Explain clauses</option>
                <option value="obligations">Key obligations</option><option value="risks">Risk flags</option><option value="questions">Questions for solicitor</option>
              </select>
              <button onClick={analyze} disabled={aiBusy} className={goldBtn} data-testid="legal-analyze">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Review</button>
            </div>
            {files.length === 0 && <p className="mt-2 text-xs text-white/35">Upload documents in the Documents section first.</p>}
          </div>

          <div className="rounded-2xl border border-white/8 bg-charcoal p-5">
            <p className="mb-3 font-heading font-semibold text-white/85">Draft a document</p>
            <div className="flex flex-col gap-2 sm:flex-row">
              <input value={draft.doc_type} onChange={(e) => setDraft({ ...draft, doc_type: e.target.value })} placeholder="e.g. NDA, Service Agreement" data-testid="legal-draft-type" className="rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm focus:outline-none sm:w-56" />
              <input value={draft.details} onChange={(e) => setDraft({ ...draft, details: e.target.value })} placeholder="Key details (parties, purpose, jurisdiction)…" data-testid="legal-draft-details" className="flex-1 rounded-xl border border-white/10 bg-graphite px-3 py-2.5 text-sm focus:outline-none" />
              <button onClick={makeDraft} disabled={aiBusy || !can("legal.edit")} className={goldBtn} data-testid="legal-draft-btn">{aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Draft</button>
            </div>
          </div>

          {aiOut && (
            <div className="rounded-2xl border border-gold/20 bg-charcoal p-5" data-testid="legal-ai-output">
              <Markdown>{aiOut}</Markdown>
              <Disclaimer />
            </div>
          )}
          {!hasFeature("legal") && <p className="text-xs text-white/35">Document comparison and chronology building are available on BDVG Business and above.</p>}
        </div>
      )}

      {tab === "templates" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <button key={t.id} onClick={() => { setTab("ai"); setDraft({ ...draft, doc_type: t.name }); toast(`Loaded ${t.name} into the drafter`); }}
              className="rounded-2xl border border-white/8 bg-charcoal p-5 text-left transition-colors hover:border-gold/25" data-testid={`legal-template-${t.id}`}>
              <FileText className="h-5 w-5 text-gold" />
              <p className="mt-3 font-medium text-white/85">{t.name}</p>
              <p className="text-xs text-white/40">{t.category}</p>
            </button>
          ))}
        </div>
      )}

      {showMatter && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm" onClick={() => setShowMatter(false)}>
          <div className="w-full max-w-md rounded-2xl border border-white/10 bg-charcoal p-6" onClick={(e) => e.stopPropagation()}>
            <h2 className="mb-4 font-heading text-lg font-semibold">New Legal Matter</h2>
            <input value={mForm.title} onChange={(e) => setMForm({ ...mForm, title: e.target.value })} placeholder="Matter title" data-testid="legal-matter-title" className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:outline-none" />
            <select value={mForm.type} onChange={(e) => setMForm({ ...mForm, type: e.target.value })} className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:outline-none">{MATTER_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
            <input value={mForm.jurisdiction} onChange={(e) => setMForm({ ...mForm, jurisdiction: e.target.value })} placeholder="Jurisdiction" className="mb-3 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:outline-none" />
            <select value={mForm.priority} onChange={(e) => setMForm({ ...mForm, priority: e.target.value })} className="mb-4 w-full rounded-xl border border-white/10 bg-graphite px-4 py-2.5 text-sm focus:outline-none"><option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option></select>
            <button onClick={createMatter} className={`${goldBtn} w-full justify-center`} data-testid="legal-matter-save">Create Matter</button>
          </div>
        </div>
      )}
    </Scroller>
  );
}
